import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _merge from 'lodash/merge';
import { Widget } from '@uploadcare/react-widget';
import effectsTab from 'uploadcare-widget-tab-effects';
import uploadcare from 'uploadcare-widget';

import { validateMinDimensions, validateMaxFileSize, validateImageType } from './validators';
import { uploadcareKey, cleanUploadcareUrls } from './utils';
import { getWidgetCopy } from './widgetCopy';

import thirdPartyTabStyles from './thirdPartyTabStyles';
import './UploadcareWidget.less';

const DEFAULT_FILE_TYPES = ['image/jpeg', 'image/png'];
const MAX_FILE_SIZE_MB = 10;
const PROGRESS_STATE_CLASS = 'zola-state-progress';
const ERROR_STATE_CLASS = 'zola-state-error';

const getPreviewTab = showEffects => {
  if (showEffects) return effectsTab;

  let unwrappedUC;
  uploadcare.plugin(internalUC => {
    unwrappedUC = internalUC;
  });

  const { PreviewTab, PreviewTabMultiple } = unwrappedUC.widget.tabs;

  // The following function is based on https://github.com/uploadcare/uploadcare-widget/blob/1b33ac45986bffeb311bdb1293170b3925932aa1/src/widget/dialog.js#L215
  return (tabPanel, tabButton, dialogApi, settings, name) => {
    if (!settings.previewStep && dialogApi.fileColl.length() === 0) {
      return () => {};
    }
    const TabClass = settings.multiple ? PreviewTabMultiple : PreviewTab;
    return new TabClass(tabPanel, tabButton, dialogApi, settings, name);
  };
};

const UploadcareWidget = ({
  allowedFileTypes = DEFAULT_FILE_TYPES,
  cachedValue,
  closeViaOverlay = true,
  cropRatio,
  isBusy = false,
  maxFileCount = 0,
  maxSize = MAX_FILE_SIZE_MB,
  minFileCount = 1,
  minDimension,
  minHeight,
  minWidth,
  onAddPhotos,
  onUploadcareFileReady,
  renderButton,
  resetAfterSubmit = false,
  showEffects = true,
}) => {
  const uploadWidget = useRef();
  const isMultiple = minFileCount > 1 || maxFileCount > 1;
  const [value, setValue] = useState();
  const [isResettingValue, setIsResettingValue] = useState(false);
  const [customTabs, setCustomTabs] = useState(null);

  const onDialogOpen = useCallback(
    ({ dialogElement, fileColl }) => {
      // eslint-disable-next-line no-underscore-dangle
      const widgetFiles = fileColl.__items;

      // Stop mousedown events from propagating outside of the dialog
      dialogElement.bind('mousedown', e => {
        e.stopPropagation();
      });

      // Sometimes, we want to make sure clients don't accidentally close the widget.
      if (!closeViaOverlay) {
        dialogElement.off('dblclick');
      }

      const updateWidgetState = () => {
        const anyFilesInProgress = widgetFiles.some(file => file.state() === 'pending');
        const anyFilesFailed = widgetFiles.some(file => file.state() === 'rejected');

        if (anyFilesInProgress) {
          dialogElement.addClass(PROGRESS_STATE_CLASS);
          dialogElement.removeClass(ERROR_STATE_CLASS);
        } else if (anyFilesFailed) {
          dialogElement.removeClass(PROGRESS_STATE_CLASS);
          dialogElement.addClass(ERROR_STATE_CLASS);
        } else {
          dialogElement.removeClass(PROGRESS_STATE_CLASS);
          dialogElement.removeClass(ERROR_STATE_CLASS);
        }
      };

      // User added a new file
      fileColl.onAdd.add(file => {
        // Immediately cancel the upload if over maxFileCount (to save time and bandwidth)
        if (maxFileCount && widgetFiles.length > maxFileCount) file.cancel();

        // When a file starts, update the widget state
        updateWidgetState();

        // When a file completed (success or failure), update the widget state
        file.always(updateWidgetState);
      });

      // User clicked file's trash icon
      fileColl.onRemove.add(file => {
        // When a pending file is removed from the list, force the upload to cancel (to save time and bandwidth)
        if (file.state() === 'pending') file.cancel();

        // When a file is removed, update the widget state
        file.always(updateWidgetState);
      });
    },
    [closeViaOverlay, maxFileCount]
  );

  useEffect(() => {
    if (cachedValue) {
      // if cachedValue is set, use it as an array of strings
      const urls = Array.isArray(cachedValue) ? cachedValue : [cachedValue];
      const validUrls = urls.filter(url => !!url);

      setValue(validUrls.length ? validUrls : null);
    } else if (isResettingValue) {
      // If reset is needed, temporarily set value to undefined. It will be set to null on the next render.
      setValue(undefined);
      setIsResettingValue(false);
    } else {
      // Otherwise, simply use the null value
      setValue(null);
    }
  }, [cachedValue, isResettingValue]);

  const openDialog = () => {
    if (isBusy) return;

    // Make sure the dialog copy contains the current widget's settings
    uploadcare.plugin(internal => {
      const filledOutCopy = getWidgetCopy({
        minDimension,
        minWidth,
        minHeight,
        maxSize,
        maxFileCount,
        allowedFileTypes,
      });

      // eslint-disable-next-line no-param-reassign
      internal.locale.translations.en = _merge(internal.locale.translations.en, filledOutCopy);
      internal.locale.rebuild();
    });

    // Hack that forces the widget to use the current customTab settings every time the widget is opened.
    setCustomTabs({ preview: getPreviewTab(showEffects) });

    // Let the widget register customTabs before opening the dialog
    setTimeout(() => {
      uploadWidget.current.openDialog();
    }, 200);
  };

  const onDialogClose = useCallback(() => {
    // Remove customTabs so they can be reset the next time the widget is opened.
    // Delayed to allow the widget modal to close before running.
    setTimeout(() => {
      setCustomTabs(null);
    }, 200);
  }, []);

  const handleFileSelect = file => {
    // If the cached image was deleted, ignore the change.
    if (!file) return;
    if (onAddPhotos) onAddPhotos();

    file.done(completedFile => {
      const updatedFile = cleanUploadcareUrls(completedFile);
      const valueisIdentical = value && updatedFile.cdnUrl === value[0];

      // Don't invoke callback if nothing changed
      if (valueisIdentical) return;

      onUploadcareFileReady(updatedFile);

      // If resetAfterSubmit, we force the widget to forget the last uploaded photos.
      if (resetAfterSubmit) {
        setIsResettingValue(true);
      }
    });
  };

  const handleFileGroupSelect = fileGroup => {
    // If all cached images were deleted, ignore the change.
    if (!fileGroup) return;
    if (onAddPhotos) onAddPhotos();

    const filePromises = fileGroup.files().map(file => {
      return new Promise(resolve => {
        file.done(completedFile => resolve(cleanUploadcareUrls(completedFile)));
      });
    });

    Promise.all([fileGroup.promise(), ...filePromises]).then(
      ([completedGroup, ...completedFiles]) => {
        const newUrls = completedFiles.map(f => f.cdnUrl);
        const valuesAreIdentical = value && newUrls.every((newUrl, i) => newUrl === value[i]);

        // Only invoke callback if images or image order change.
        if (valuesAreIdentical) return;

        onUploadcareFileReady(completedGroup.uuid, completedFiles);

        // If resetAfterSubmit, we force the widget to forget the last uploaded photos.
        if (resetAfterSubmit) {
          setIsResettingValue(true);
        }
      }
    );
  };

  return (
    <>
      {renderButton && renderButton(isBusy, !!value, openDialog)}
      <Widget
        multiple={isMultiple}
        multipleMax={maxFileCount}
        multipleMin={minFileCount}
        value={value}
        ref={uploadWidget}
        publicKey={uploadcareKey}
        preloader={null}
        imagesOnly
        previewStep
        customTabs={customTabs}
        effects={`${cropRatio ? 'crop,' : ''}mirror,enhance,sharp,blur,grayscale`}
        crop={cropRatio}
        preferredTypes={allowedFileTypes.join(' ')}
        tabs="file dropbox gphotos" // TODO: get from props
        tabsCss={thirdPartyTabStyles}
        onFileSelect={isMultiple ? handleFileGroupSelect : handleFileSelect}
        onDialogOpen={onDialogOpen}
        onDialogClose={onDialogClose}
        validators={[
          validateMinDimensions(minWidth, minHeight, minDimension),
          validateMaxFileSize(maxSize),
          validateImageType(allowedFileTypes),
        ]}
      />
    </>
  );
};

UploadcareWidget.propTypes = {
  allowedFileTypes: PropTypes.arrayOf(PropTypes.string),
  cachedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  cropRatio: PropTypes.string,
  isBusy: PropTypes.bool,
  maxFileCount: PropTypes.number,
  maxSize: PropTypes.number,
  minFileCount: PropTypes.number,
  minDimension: PropTypes.number,
  minHeight: PropTypes.number,
  minWidth: PropTypes.number,
  onAddPhotos: PropTypes.func,
  onUploadcareFileReady: PropTypes.func.isRequired,
  renderButton: PropTypes.func.isRequired,
  resetAfterSubmit: PropTypes.bool,
  showEffects: PropTypes.bool,
};

export default UploadcareWidget;



// WEBPACK FOOTER //
// ./src/components/common/UploadcareWidget/UploadcareWidget.jsx